import BaseMap from 'components/BaseMap/BaseMap';
import PlacesSearch from 'components/Header/PlacesSearch';
import ConfirmationModal from 'components/Modals/ConfirmationModal';
import { usePerms } from 'contexts/PermsContext';
import useLocation from 'hooks/useLocation';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Button } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import { NAVIGATION_STYLES } from 'store/config/displayOptions.config';
import { selectNavigationStyle } from 'store/selectors/DisplayOptions/displayOptionsSelectors';
import { selectUserSelectedLocationPlaceId } from 'store/selectors/User/userSelectors';
import PlacesLayout from './components/PlacesLayout/PlacesLayout';
import SavedPlacesTable from './components/SavedPlacesTable/SavedPlacesTable';
import usePlaces from './hooks/usePlaces';
import './assets/places.scss';

/** Manage saved places and their map preview using the selected navigation appearance. */
const Places = ({ search = '' }) => {
  const {
    places,
    loadingPlaces,
    fetchingPlaces,
    addingPlaces,
    selectingPlace,
    fullSelectedPlaceObject,
    selectPlace,
    addPlace,
    removePlace,
    renamePlace,
    changeHomePlace,
  } = usePlaces();
  const navigationStyle = useSelector(selectNavigationStyle);
  const userSelectedPlaceId = useSelector(selectUserSelectedLocationPlaceId);
  const { locations } = useLocation();
  const perms = usePerms();
  const canEditPlaces = perms.testPermission([8, 9, 10, 11, 12, 13], ['Create-EditPlaces']);
  const canChangeHomePlace = perms.testPermission([8, 9, 10, 11, 12, 13], ['HomeLocationEditable']);
  const isClassic = navigationStyle === NAVIGATION_STYLES.CLASSIC;
  const [selectedPlace, setSelectedPlace] = useState(null);
  const [previewPlace, setPreviewPlace] = useState(null);
  const [placeToDelete, setPlaceToDelete] = useState(null);
  const [error, setError] = useState('');
  const [locationLatitude, locationLongitude] = locations.effectiveLocation.coords;
  const mapCenter = useMemo(
    () =>
      previewPlace
        ? [previewPlace.latitude, previewPlace.longitude]
        : [locationLatitude, locationLongitude],
    [previewPlace, locationLatitude, locationLongitude],
  );

  // Keep the preview aligned with loaded places without changing the app's active location.
  useEffect(() => {
    if (loadingPlaces || fetchingPlaces) return;
    setSelectedPlace(fullSelectedPlaceObject || places[0] || null);
  }, [loadingPlaces, fetchingPlaces, places, fullSelectedPlaceObject]);

  useEffect(() => {
    if (!selectedPlace) return;
    setPreviewPlace(selectedPlace);
  }, [selectedPlace]);

  const savePreviewPlace = async () => {
    if (!canEditPlaces || !previewPlace?.place_id || addingPlaces || selectingPlace) return;
    setError('');

    try {
      const savedPlace = places.find((place) => place.place_id === previewPlace.place_id);

      if (savedPlace) {
        await selectPlace(savedPlace);
        setSelectedPlace(savedPlace);
      } else {
        await addPlace(previewPlace);
      }
    } catch {
      setError('Unable to save this place. Please try again.');
    }
  };

  const renameSavedPlace = useCallback(
    async (place, title) => {
      if (!canEditPlaces || !place?.place_id || !title) return false;
      setError('');

      try {
        await renamePlace(place.place_id, title);
        return true;
      } catch {
        setError('Unable to rename this place. Please try again.');
        return false;
      }
    },
    [canEditPlaces, renamePlace],
  );

  const changeHomeLocation = useCallback(
    async (place) => {
      if (!canChangeHomePlace || !place?.place_id || place.DefaultLocationUserHome) return null;
      setError('');

      try {
        const newHomePlace = await changeHomePlace(place);
        if (newHomePlace) setSelectedPlace(newHomePlace);
        return newHomePlace;
      } catch {
        setError('Unable to change your home place. Please try again.');
        return null;
      }
    },
    [canChangeHomePlace, changeHomePlace],
  );

  const deleteSavedPlace = async () => {
    if (!canEditPlaces || !placeToDelete?.place_id || placeToDelete.DefaultLocationUserHome) return;
    setError('');
    setPlaceToDelete(null);

    try {
      const remainingPlaces = await removePlace(placeToDelete.place_id);
      if (!remainingPlaces) return;

      const nextSelectedPlace =
        remainingPlaces.find((place) => place.place_id === userSelectedPlaceId) ||
        remainingPlaces[0] ||
        null;
      setSelectedPlace(nextSelectedPlace);

      if (nextSelectedPlace && nextSelectedPlace.place_id !== userSelectedPlaceId) {
        await selectPlace(nextSelectedPlace);
      }
    } catch {
      setError('Unable to finish deleting this place. Please try again.');
    }
  };

  return (
    <>
      {error && (
        <p role="alert" className="text-danger mb-0">
          {error}
        </p>
      )}
      {placeToDelete && (
        <ConfirmationModal
          show
          handleShow={() => setPlaceToDelete(null)}
          bodyTxt="Are you sure you want to delete this place?"
          cancelBtnTxt="No"
          confirmBtnTxt="Yes"
          confirmHandler={deleteSavedPlace}
        />
      )}
      <PlacesLayout
        isClassic={isClassic}
        canEditPlaces={canEditPlaces}
        searchControl={
          <PlacesSearch
            noBack
            setSearching={() => {}}
            addLocation={setPreviewPlace}
            quickSave
            cwValidated
          />
        }
        mapPreview={<BaseMap mapCenter={mapCenter} zoom={11} disableMapReset />}
        selectedAddress={previewPlace?.place_data?.formatted_address}
        saveButton={
          <Button
            onClick={savePreviewPlace}
            disabled={!previewPlace || addingPlaces || selectingPlace}
            variant="primary"
          >
            Save
          </Button>
        }
      >
        <SavedPlacesTable
          places={places}
          search={search}
          selectedPlaceId={selectedPlace?.place_id}
          canEditPlaces={canEditPlaces}
          canChangeHomePlace={canChangeHomePlace}
          isClassic={isClassic}
          onSelectPlace={setSelectedPlace}
          onRenamePlace={renameSavedPlace}
          onChangeHomePlace={changeHomeLocation}
          onDeletePlace={setPlaceToDelete}
        />
      </PlacesLayout>
    </>
  );
};

export default Places;
